/**
 * Pure helpers for the chat hook — no React, no MSAL/apiClient imports, so they
 * stay trivially unit-testable. The hook (useChatLogic) owns all state + queries.
 */
import type { AgentThreadMessage, AgentThreadSummary } from '../../lib/types.js';
import type { InfiniteData } from '@tanstack/react-query';
import type { ChatMessage, Conversation } from './types.js';

export const NEW_CHAT_TITLE = 'New chat';

const MAX_TITLE = 40;

/** Truncated first line of a message — the optimistic sidebar label for a new chat. */
export const titleFromMessage = (text: string): string => {
  const trimmed = text.trim();
  if (!trimmed) return NEW_CHAT_TITLE;
  return trimmed.length > MAX_TITLE ? `${trimmed.slice(0, MAX_TITLE)}…` : trimmed;
};

/**
 * Sidebar list = not-yet-sent local drafts first, then the server threads
 * (already newest-first). Deduped by id: once a draft is sent it shows up in the
 * server list, so we drop the local copy to avoid a double entry.
 */
export const mergeConversations = (
  local: Conversation[],
  server: AgentThreadSummary[],
): Conversation[] => {
  const serverIds = new Set(server.map((t) => t.id));
  const localOnly = local.filter((c) => !serverIds.has(c.id));
  const serverConvos: Conversation[] = server.map((t) => ({
    id: t.id,
    title: t.title || NEW_CHAT_TITLE,
  }));
  return [...localOnly, ...serverConvos];
};

/**
 * Server messages → UI messages, with stable per-thread render keys. Carries the
 * server message id as `feedbackId` so a replayed assistant turn keeps its 👍/👎
 * control (and its rated state) after a reload — the correlation is always present.
 */
export const toChatMessages = (messages: AgentThreadMessage[]): ChatMessage[] =>
  messages.map((m) => ({
    // The server id, so a message keeps its key across pages and refetches.
    id: m.id,
    role: m.role,
    content: m.content,
    feedbackId: m.id,
    ...(m.bot ? { bot: m.bot } : {}),
    ...(m.question ? { question: m.question } : {}),
    // Replayed markers become the settled activity record: past-tense labels
    // and details come from the server; timing isn't persisted, so it's absent.
    ...(m.toolMarkers?.length
      ? {
          toolActivity: m.toolMarkers.map((t, j) => ({
            id: `${m.id}-tool-${j}`,
            label: t.label,
            ...(t.detail != null ? { detail: t.detail } : {}),
            status: 'done' as const,
          })),
        }
      : {}),
  }));

/**
 * Lock the bot question a message answers, as the server will: the tapped one by id, else the
 * newest one still open (a typed reply answers it). Unchanged when nothing is open.
 */
export const markQuestionAnswered = (
  messages: ChatMessage[],
  text: string,
  tap?: { questionId: string; value?: string },
): ChatMessage[] => {
  const target = tap
    ? messages.find((m) => m.question?.id === tap.questionId)
    : [...messages].reverse().find((m) => m.question?.status === 'open');
  if (!target?.question || target.question.status !== 'open') return messages;
  const answered =
    tap?.value !== undefined
      ? { kind: 'option' as const, value: tap.value }
      : { kind: 'text' as const, text };
  return messages.map((m) =>
    m === target && m.question
      ? { ...m, question: { ...m.question, status: 'answered' as const, answered } }
      : m,
  );
};

/** One loaded page of a thread, oldest first. */
export interface MessagesPage {
  messages: ChatMessage[];
  nextBefore: string | null;
}

/** A thread's loaded pages: the newest first, each older one after it. */
export type ThreadPages = InfiniteData<MessagesPage, string | null>;

/** Every loaded message, oldest first. A message on two pages (two written at the same
 *  instant, across a page edge) shows once, as the newer page has it. */
export const flattenPages = (data: ThreadPages | undefined): ChatMessage[] => {
  const seen = new Set<string>();
  return (data?.pages ?? [])
    .map((page) =>
      page.messages.filter((m) => {
        if (seen.has(m.id)) return false;
        seen.add(m.id);
        return true;
      }),
    )
    .reverse()
    .flat();
};

/** Change the newest page, where a live turn and a sent message land. */
export const withNewestPage = (
  data: ThreadPages | undefined,
  change: (messages: ChatMessage[]) => ChatMessage[],
): ThreadPages => {
  const [newest, ...older] = data?.pages ?? [];
  return {
    pages: [
      { messages: change(newest?.messages ?? []), nextBefore: newest?.nextBefore ?? null },
      ...older,
    ],
    pageParams: data?.pageParams ?? [null],
  };
};
